import React from "react"
import { Link } from "gatsby"
import { ChevronRightIcon } from "@heroicons/react/solid"
import {
  GlobeAltIcon,
  SearchIcon,
  TagIcon,
  ChartBarIcon,
  StarIcon,
  LightningBoltIcon,
} from "@heroicons/react/outline"

const benefits = [
  {
    icon: GlobeAltIcon,
    title: "A page of your own",
    description:
      "Every community gets a dedicated, high-traffic listing page on an established domain. More surface area for people to find you.",
  },
  {
    icon: SearchIcon,
    title: "High-intent discovery",
    description:
      "Hive Index visitors are actively looking for communities to join. You get in front of people who already want what you offer.",
  },
  {
    icon: TagIcon,
    title: "Feature spotlight",
    description:
      "Showcase what makes your community worth joining: events, jobs boards, courses, mentorship, perks, and more.",
  },
  {
    icon: ChartBarIcon,
    title: "Listing analytics",
    description:
      "See how many seekers view your listing and click through to your community. Know what is working.",
  },
  {
    icon: StarIcon,
    title: "Reviews and ratings",
    description:
      "Members can leave reviews, building social proof that converts future browsers into joiners.",
  },
  {
    icon: LightningBoltIcon,
    title: "Free, forever",
    description:
      "No cost to list. No catch. Paid placement options exist for those who want more visibility, but the core listing is always free.",
  },
]

const SubmitSection = ({ communityCount }) => {
  return (
    <section
      className="my-14 sm:my-16 lg:my-20 reveal"
      aria-labelledby="submit-section-heading"
    >
      <div className="bg-yellow-50 border border-yellow-100 rounded-xl overflow-hidden">
        <div className="px-6 py-10 sm:px-10 sm:py-14 lg:grid lg:grid-cols-2 lg:gap-16 lg:items-start">
          {/* Left: headline + CTA */}
          <div>
            <p className="section-eyebrow">For community builders</p>
            <h2
              id="submit-section-heading"
              className="mt-3 text-2xl font-extrabold text-gray-900 tracking-tight sm:text-3xl lg:text-4xl"
            >
              Your community belongs here
            </h2>
            <p className="mt-5 text-base sm:text-lg text-gray-600 leading-relaxed">
              Join{" "}
              {communityCount ? (
                <strong>{communityCount.toLocaleString()}+ communities</strong>
              ) : (
                "thousands of communities"
              )}{" "}
              already listed. Whether you run a Discord server, a Slack group,
              a Circle space, or your own custom platform, a Hive Index listing
              puts you in front of people actively searching for a community like
              yours.
            </p>
            <p className="mt-4 text-base sm:text-lg text-gray-600">
              Listing takes less than five minutes. No account needed.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/submit/"
                className="inline-flex items-center justify-center px-6 py-3 bg-rose-600 text-white font-semibold rounded-md shadow-sm hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-2 transition-colors"
              >
                List your community for free
                <ChevronRightIcon className="ml-2 h-5 w-5" aria-hidden="true" />
              </Link>
              <Link
                to="/communities/"
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-gray-700 font-semibold rounded-md border border-gray-200 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-2 transition-colors"
              >
                Browse the directory
              </Link>
            </div>

            {/* <p className="mt-6 text-sm text-gray-500">
              Already listed?{" "}
              <Link
                to="/submit/"
                className="text-rose-600 font-semibold hover:underline"
              >
                Claim your listing
              </Link>{" "}
              to edit it, add your logo, and see analytics.
            </p> */}
          </div>

          {/* Right: benefits grid */}
          <div className="mt-10 lg:mt-0">
            <ul className="space-y-6" aria-label="Benefits of listing on Hive Index">
              {benefits.map((benefit) => (
                <li key={benefit.title} className="flex gap-4">
                  <div className="flex-shrink-0 mt-0.5">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-rose-100">
                      <benefit.icon
                        className="h-5 w-5 text-rose-600"
                        aria-hidden="true"
                      />
                    </span>
                  </div>
                  <div>
                    <p className="text-base font-semibold text-gray-900">
                      {benefit.title}
                    </p>
                    <p className="mt-1 text-sm sm:text-base text-gray-600 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  )
}

export default SubmitSection
